import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Download, Trash2, Eye, RefreshCw, BadgeCheck, Calendar,
  Lock, Unlock, Printer, X, AlertCircle, MessageCircle, Send,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  "Recibido", "Pendiente de revisar", "Falta información",
  "Pago solicitado", "Listo para imprimir", "En preparación", "Terminado",
  "Entregado", "Cancelado", "Incidencia",
];

const statusColors = {
  "Recibido": "bg-blue-100 text-blue-800",
  "Pendiente de revisar": "bg-yellow-100 text-yellow-800",
  "Falta información": "bg-orange-100 text-orange-800",
  "Pago solicitado": "bg-blue-100 text-blue-800",
  "Listo para imprimir": "bg-purple-100 text-purple-800",
  "En preparación": "bg-indigo-100 text-indigo-800",
  "Terminado": "bg-green-100 text-green-800",
  "Entregado": "bg-slate-200 text-slate-800",
  "Cancelado": "bg-red-100 text-red-800",
  "Incidencia": "bg-red-200 text-red-900",
};

function isOpenOrder(o) {
  if (!o?.is_open) return false;
  if (!o.expires_at) return false;
  return new Date(o.expires_at) > new Date();
}

function formatKapeiDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).replace(",", " -") + " h";
}

function pickupUrgencyLabel(value) {
  if (!value) return "Recoger";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Recoger";

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (d < startToday) return "Recogida ATRASADA";
  if (sameDay(d, today)) return "Recoger HOY";
  if (sameDay(d, tomorrow)) return "Recoger MAÑANA";
  return "Recoger";
}

function pickupUrgencyClass(value) {
  const label = pickupUrgencyLabel(value);
  if (label.includes("ATRASADA")) return "bg-red-600 text-white";
  if (label.includes("HOY")) return "bg-purple-600 text-white";
  if (label.includes("MAÑANA")) return "bg-orange-500 text-white";
  return "bg-[#d8b4e2]/40 text-[#7c3aed]";
}

const CLOSED_STATUSES = new Set(["Terminado", "Entregado", "Cancelado"]);

function clientGroupKey(o) {
  return String(o?.user_id || o?.client_email || o?.client_phone || o?.client_name || "")
    .trim()
    .toLowerCase();
}

function isGroupPayableOrder(o) {
  return (
    o &&
    !CLOSED_STATUSES.has(o.status) &&
    o.payment_status === "pending" &&
    Number(o.payment_amount_eur || 0) > 0
  );
}

const PRODUCTION_RULES = {
  bwPage: 1,
  colorPage: 5,
  sizeMultiplier: {
    A4: 1,
    A3: 2,
    A2: 110,
  },
  paperExtra: {
    normal: 0,
    cartulina: 3,
    brillante: 10,
    "brillante 350": 15,
    texturizado: 20,
  },
  lamination: 17,
  binding: 25,
  photo10x15: 7,
  photo15x20: 14,
  signalThresholdNormal: 250,
  signalThresholdPremium: 500,
};

function _norm(v) {
  return String(v || "").trim().toLowerCase();
}

function _copies(o) {
  const n = Number(o?.options?.copies || 1);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function _sizeMultiplier(size) {
  const key = String(size || "A4").toUpperCase();
  return PRODUCTION_RULES.sizeMultiplier[key] || 1;
}

function _paperExtra(paper) {
  const v = _norm(paper);
  if (!v) return 0;
  if (v.includes("350") && v.includes("brill")) return PRODUCTION_RULES.paperExtra["brillante 350"];
  if (v.includes("brill")) return PRODUCTION_RULES.paperExtra.brillante;
  if (v.includes("cart")) return PRODUCTION_RULES.paperExtra.cartulina;
  if (v.includes("text")) return PRODUCTION_RULES.paperExtra.texturizado;
  return PRODUCTION_RULES.paperExtra.normal;
}

function productionAnalysisForOrders(items, premium = false) {
  const active = (items || []).filter((o) => o && !CLOSED_STATUSES.has(o.status));
  const out = {
    orders: active.length,
    filePages: 0,
    printedUnits: 0,
    bwUnits: 0,
    colorUnits: 0,
    unspecifiedUnits: 0,
    undefinedFiles: [],
    points: 0,
    signalRecommended: false,
    level: "BAJA",
  };

  active.forEach((o) => {
    const opts = o.options || {};
    const copies = _copies(o);
    const pages = Number(o.page_count || 0);
    const units = pages * copies;
    const sizeMult = _sizeMultiplier(opts.size);
    const paperExtra = _paperExtra(opts.paper);

    out.filePages += pages;
    out.printedUnits += units;

    if (opts.color === "bw") {
      out.bwUnits += units;
      out.points += units * PRODUCTION_RULES.bwPage * sizeMult;
    } else if (opts.color === "color") {
      out.colorUnits += units;
      out.points += units * PRODUCTION_RULES.colorPage * sizeMult;
    } else {
      out.unspecifiedUnits += units;
      out.points += units * 2 * sizeMult;

      const files = o.files || [];
      if (files.length) {
        files.forEach((f) => {
          out.undefinedFiles.push({
            orderId: o.id,
            code: o.code,
            filename: f.filename || "archivo",
            pages: typeof f.pages === "number" ? f.pages : null,
          });
        });
      } else {
        out.undefinedFiles.push({
          orderId: o.id,
          code: o.code,
          filename: "Sin archivos registrados",
          pages: null,
        });
      }
    }

    if (paperExtra > 0) {
      out.points += units * paperExtra * sizeMult;
    }
  });

  if (out.points >= 500) out.level = "MUY ALTA";
  else if (out.points >= 250) out.level = "ALTA";
  else if (out.points >= 100) out.level = "MEDIA";

  const threshold = premium ? PRODUCTION_RULES.signalThresholdPremium : PRODUCTION_RULES.signalThresholdNormal;
  out.signalRecommended = out.points >= threshold || out.colorUnits >= 100 || out.bwUnits >= 500;

  return out;
}

function isPremiumClient(o) {
  return !!(o?.is_reliable || o?.client_is_reliable || o?.user_is_reliable);
}

function catalogItemBySlug(catalog, slug) {
  return (catalog || []).find((item) => item.slug === slug);
}

function unitPriceForQty(item, qty) {
  const rules = item?.price_rules || [];
  const n = Number(qty || 0);
  const found = rules.find((r) => {
    const from = Number(r.from || 0);
    const to = r.to === null || r.to === undefined ? Infinity : Number(r.to);
    return n >= from && n <= to;
  });
  return Number(found?.unit_price_eur || item?.price_eur || 0);
}

function priceSummaryForCommercialGroup(group, catalog) {
  const bwItem = catalogItemBySlug(catalog, "impresion-bn-a4");
  const colorItem = catalogItemBySlug(catalog, "impresion-color-a4");

  const lines = [];
  let total = 0;

  if (group.bwUnits > 0 && bwItem) {
    const unitPrice = unitPriceForQty(bwItem, group.bwUnits);
    const amount = group.bwUnits * unitPrice;
    total += amount;
    lines.push({
      label: "Impresión B/N A4",
      qty: group.bwUnits,
      unitPrice,
      amount,
    });
  }

  if (group.colorUnits > 0 && colorItem) {
    const unitPrice = unitPriceForQty(colorItem, group.colorUnits);
    const amount = group.colorUnits * unitPrice;
    total += amount;
    lines.push({
      label: "Impresión Color A4",
      qty: group.colorUnits,
      unitPrice,
      amount,
    });
  }

  return {
    lines,
    total: Math.round(total * 100) / 100,
  };
}

function automaticQuoteForGroups(groups, catalog) {
  const groupQuotes = (groups || []).map((g) => ({
    group: g,
    price: priceSummaryForCommercialGroup(g, catalog),
  }));

  groupQuotes.forEach((q) => {
    const calculatedOrderIds = new Set();
    if (q.price.lines.length > 0) {
      (q.group.orders || []).forEach((o) => calculatedOrderIds.add(o.id));
    }

    (q.group.orders || []).forEach((o) => {
      const manual = Number(o.work_amount_eur || 0);
      if (manual > 0 && !calculatedOrderIds.has(o.id)) {
        q.price.lines.push({
          label: `Importe manual ${o.code || ""}`.trim(),
          qty: 1,
          unitPrice: manual,
          amount: manual,
        });
        q.price.total = Math.round((Number(q.price.total || 0) + manual) * 100) / 100;
      }
    });
  });

  const total = groupQuotes.reduce((sum, q) => sum + Number(q.price.total || 0), 0);

  const byOrderId = {};
  groupQuotes.forEach((q) => {
    if (q.group.orders?.length === 1) {
      byOrderId[q.group.orders[0].id] = q.price.total;
    }
  });

  return {
    groupQuotes,
    total: Math.round(total * 100) / 100,
    byOrderId,
  };
}

function commercialGroupsForOrders(items, mode = "grouped") {
  const active = (items || []).filter((o) => o && !CLOSED_STATUSES.has(o.status));

  const makeSummary = (orders, id, label) => {
    const out = {
      id,
      label,
      orders,
      orderCount: orders.length,
      filePages: 0,
      printedUnits: 0,
      bwUnits: 0,
      colorUnits: 0,
      unspecifiedUnits: 0,
    };

    orders.forEach((o) => {
      const copies = _copies(o);
      const pages = Number(o.page_count || 0);
      const units = pages * copies;

      out.filePages += pages;
      out.printedUnits += units;

      if (o?.options?.color === "bw") out.bwUnits += units;
      else if (o?.options?.color === "color") out.colorUnits += units;
      else out.unspecifiedUnits += units;
    });

    return out;
  };

  if (mode === "separate") {
    return active.map((o) => makeSummary([o], o.id, o.code || "Pedido"));
  }

  return [makeSummary(active, "grouped", "Grupo comercial")];
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [askedPrinted, setAskedPrinted] = useState(false); // shows the "¿Has impreso?" prompt once per open
  const [staffReply, setStaffReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [editingPermanentNote, setEditingPermanentNote] = useState(false);
  const [permanentNoteText, setPermanentNoteText] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [workAmount, setWorkAmount] = useState("");
  const [productionInstructions, setProductionInstructions] = useState("");
  const [showProductionInstructions, setShowProductionInstructions] = useState(false);
  const [clientWorkTab, setClientWorkTab] = useState("production");
  const [pricingMode, setPricingMode] = useState("grouped");
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [commercialExtras, setCommercialExtras] = useState([]);
  const [extraConcept, setExtraConcept] = useState("");
  const [extraAmount, setExtraAmount] = useState("");
  const [basketSearch, setBasketSearch] = useState("");
  const [basketSelectedService, setBasketSelectedService] = useState(null);
  const [basketQty, setBasketQty] = useState("1");
  const [basketUnitPrice, setBasketUnitPrice] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [paymentType, setPaymentType] = useState("full");
  const [paymentNote, setPaymentNote] = useState("");
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [savingBasket, setSavingBasket] = useState(false);
  const [siblingOrders, setSiblingOrders] = useState([]);
  const [savingAmount, setSavingAmount] = useState(false);

  const clientAnalysisOrders = selected
    ? [
        selected,
        ...siblingOrders.filter((o) => o.id !== selected.id),
      ]
    : siblingOrders;

  const addCatalogBasketLine = () => {
    if (!basketSelectedService) {
      toast.error("Selecciona un servicio del catálogo");
      return;
    }

    const qty = Number(String(basketQty || "").replace(",", "."));
    const unitPrice = Number(String(basketUnitPrice || "").replace(",", "."));

    if (!qty || Number.isNaN(qty) || qty <= 0) {
      toast.error("Introduce una cantidad válida");
      return;
    }
    if (Number.isNaN(unitPrice) || unitPrice < 0) {
      toast.error("Introduce un precio válido");
      return;
    }

    setCommercialExtras((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        type: "catalog",
        concept: `${basketSelectedService.icon || "📦"} ${basketSelectedService.name}`,
        service_slug: basketSelectedService.slug,
        qty,
        unitPrice,
        amount: Math.round(qty * unitPrice * 100) / 100,
      },
    ]);

    setBasketSearch("");
    setBasketSelectedService(null);
    setBasketQty("1");
    setBasketUnitPrice("");
  };

  const selectBasketService = (service) => {
    setBasketSelectedService(service);
    setBasketSearch("");
    setBasketUnitPrice(service.price_eur !== null && service.price_eur !== undefined ? String(service.price_eur).replace(".", ",") : "0");
  };

  const addCommercialExtra = () => {
    const amount = Number(String(extraAmount || "").replace(",", "."));
    const concept = String(extraConcept || "").trim();

    if (!concept) {
      toast.error("Escribe el concepto del extra");
      return;
    }
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      toast.error("Introduce un importe válido para el extra");
      return;
    }

    setCommercialExtras((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        concept,
        amount,
      },
    ]);
    setExtraConcept("");
    setExtraAmount("");
  };

  const removeCommercialExtra = (id) => {
    setCommercialExtras((prev) => prev.filter((x) => x.id !== id));
  };

  const saveCommercialBasket = async ({ quote, commercialGroups, basketTotal }) => {
    try {
      setSavingBasket(true);

      const orderIds = Array.from(new Set(
        (commercialGroups || []).flatMap((g) => (g.orders || []).map((o) => o.id)).filter(Boolean)
      ));

      const lines = (quote.groupQuotes || []).flatMap(({ group, price }) =>
        (price.lines || []).map((line) => ({
          type: "automatic",
          group_id: group.id,
          group_label: group.label,
          label: line.label,
          qty: line.qty,
          unit_price_eur: Math.round(Number(line.unitPrice || 0) * 1000) / 1000,
          amount_eur: Math.round(Number(line.amount || 0) * 100) / 100,
        }))
      );

      const extras = (commercialExtras || []).map((x) => ({
        type: x.type || "manual",
        concept: x.concept,
        service_slug: x.service_slug || "",
        qty: x.qty || 1,
        unit_price_eur: x.unitPrice || x.amount,
        amount_eur: Math.round(Number(x.amount || 0) * 100) / 100,
      }));

      const { data } = await api.post("/admin/commercial-baskets", {
        order_ids: orderIds,
        pricing_mode: pricingMode,
        lines,
        extras,
        total_eur: basketTotal,
        description: `Presupuesto KAPEI: ${(clientAnalysisOrders || []).map((o) => o.code).filter(Boolean).join(", ")}`,
        status: "draft",
      });

      toast.success(`Presupuesto guardado: ${data.code}`);

      const patch = {
        last_commercial_basket_id: data.id,
        last_commercial_basket_code: data.code,
        last_commercial_basket_total_eur: data.total_eur,
      };

      setSelected((cur) => cur ? { ...cur, ...patch } : cur);
      setOrders((prev) => prev.map((o) => orderIds.includes(o.id) ? { ...o, ...patch } : o));

      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar el presupuesto");
    } finally {
      setSavingBasket(false);
    }
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    const url = filter === "all" ? "/admin/orders" : `/admin/orders?status=${encodeURIComponent(filter)}`;
    const { data } = await api.get(url);
    setOrders(data);
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    load();
    api.get("/admin/service-catalog")
      .then(({ data }) => setServiceCatalog(data || []))
      .catch(() => setServiceCatalog([]));
    const t = setInterval(() => load(true), 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const getPublicPaymentUrl = (order) => {
    if (!order?.code) return "";
    return `${window.location.origin}/pagos/${encodeURIComponent(order.code)}`;
  };

  const copyPublicPaymentUrl = async () => {
    if (!selected) return;
    const url = getPublicPaymentUrl(selected);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace KAPEI copiado");
    } catch (_) {
      toast.error("No se pudo copiar el enlace");
    }
  };

  const openPaymentWhatsApp = () => {
    if (!selected) return;
    const url = getPublicPaymentUrl(selected);
    const phone = (selected.client_phone || "").replace(/\D/g, "");
    const amount = Number(selected.payment_amount_eur || paymentAmount || 0).toFixed(2);
    const text = `Hola ${selected.client_name || ""}, puedes realizar el pago seguro de tu pedido ${selected.code} (${amount} €) aquí: ${url}`;
    const target = phone
      ? `https://wa.me/34${phone.replace(/^34/, "")}?text=${encodeURIComponent(text)}`
      : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const createPaymentRequest = async () => {
    if (!selected) return;
    const normalized = String(paymentAmount || "").replace(",", ".").trim();
    const amount = Number(normalized);
    if (!amount || Number.isNaN(amount) || amount <= 0) {
      toast.error("Introduce un importe válido");
      return;
    }

    try {
      setCreatingPayment(true);
      const { data } = await api.post(`/admin/orders/${selected.id}/payment-request`, {
        amount_eur: amount,
        description: paymentDescription || `Pedido KAPEI ${selected.code}`,
        payment_type: paymentType,
        payment_note: paymentNote,
      });
      toast.success("Enlace de pago creado y enviado al cliente");
      setSelected({
        ...selected,
        payment_status: data.status,
        payment_amount_eur: data.amount_eur,
        payment_request_id: data.id,
        payment_checkout_url: data.stripe_checkout_url,
        payment_type: data.payment_type,
        payment_note: data.payment_note,
        status: "Pago solicitado",
      });
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el pago");
    } finally {
      setCreatingPayment(false);
    }
  };

  const cancelPaymentRequest = async () => {
    if (!selected) return;
    if (!window.confirm("¿Cancelar la solicitud de pago de este pedido?")) return;

    try {
      setCreatingPayment(true);
      const { data } = await api.post(`/admin/orders/${selected.id}/payment-cancel`);
      toast.success("Solicitud de pago cancelada");
      setSelected(data);
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cancelar el pago");
    } finally {
      setCreatingPayment(false);
    }
  };

  const loadSiblingOrders = async (orderId) => {
    try {
      const { data } = await api.get(`/admin/order-siblings/${orderId}`);
      setSiblingOrders(data.orders || []);
    } catch (_) {
      setSiblingOrders([]);
    }
  };

  const saveOrderAmount = async () => {
    if (!selected) return;
    const normalized = String(workAmount || "").replace(",", ".").trim();
    const amount = Number(normalized);
    if (Number.isNaN(amount) || amount < 0) {
      toast.error("Introduce un importe válido");
      return;
    }

    try {
      setSavingAmount(true);
      const { data } = await api.patch(`/admin/orders/${selected.id}/amount`, {
        amount_eur: amount,
        production_instructions: productionInstructions,
      });
      toast.success("Importe del trabajo guardado");
      setSelected(data);
      setWorkAmount(data.work_amount_eur ? String(data.work_amount_eur).replace(".", ",") : "");
      setProductionInstructions(data.production_instructions || "");
      setOrders((prev) => prev.map((o) => o.id === data.id ? data : o));
      await loadSiblingOrders(selected.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar el importe");
    } finally {
      setSavingAmount(false);
    }
  };

  const openSiblingOrder = (orderId) => {
    const found = orders.find((o) => o.id === orderId);
    if (found) {
      openDialog(found);
    } else {
      toast.error("Recarga pedidos para abrir este KP");
    }
  };

  const paymentGroups = Object.values(
    orders.filter(isGroupPayableOrder).reduce((acc, o) => {
      const key = clientGroupKey(o);
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = {
          key,
          client_name: o.client_name || "Cliente",
          client_phone: o.client_phone || "",
          client_email: o.client_email || "",
          orders: [],
        };
      }
      acc[key].orders.push(o);
      return acc;
    }, {})
  )
    .filter((g) => g.orders.length >= 2)
    .map((g) => ({
      ...g,
      total: g.orders.reduce((sum, o) => sum + Number(o.payment_amount_eur || 0), 0),
      codes: g.orders.map((o) => o.code),
    }))
    .sort((a, b) => b.total - a.total);

  const createGroupedPayment = async (group) => {
    const codes = group.codes.join(", ");
    if (!window.confirm(`¿Crear un único pago agrupado para ${group.client_name}?\n\nPedidos: ${codes}\nTotal: ${group.total.toFixed(2)} €`)) return;

    try {
      setCreatingPayment(true);
      const { data } = await api.post("/admin/order-payment-groups", {
        order_ids: group.orders.map((o) => o.id),
        description: `Pago agrupado KAPEI: ${codes}`,
      });
      toast.success(`Pago agrupado creado: ${data.code}`);
      await load(true);

      const url = `${window.location.origin}/pagos/${encodeURIComponent(data.code)}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Enlace agrupado copiado");
      } catch (_) {}
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el pago agrupado");
    } finally {
      setCreatingPayment(false);
    }
  };

  const filtered = orders
    .filter((o) =>
      !search || o.code.toLowerCase().includes(search.toLowerCase()) ||
      o.client_name?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (!!b.has_unread_for_staff !== !!a.has_unread_for_staff) {
        return b.has_unread_for_staff ? 1 : -1;
      }
      return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
    });

  const openDialog = async (o) => {
    setSelected(o);
    setStaffReply("");
    setPermanentNoteText(o.client_internal_notes || "");
    setEditingPermanentNote(false);
    setPaymentAmount(o.payment_amount_eur ? String(o.payment_amount_eur).replace(".", ",") : "");
    setWorkAmount(o.work_amount_eur ? String(o.work_amount_eur).replace(".", ",") : "");
    setProductionInstructions(o.production_instructions || "");
    setShowProductionInstructions(!!o.production_instructions);
    setPaymentDescription(`Pedido KAPEI ${o.code}`);
    setSiblingOrders([]);
    loadSiblingOrders(o.id);
    setAskedPrinted(!!o.viewed_at); // if already viewed, don't show the prompt again
    setClientWorkTab("production");

    // En cuanto KAPEI abre el pedido, lo marcamos como visualizado.
    // Así el cliente no puede añadir archivos mientras el staff lo revisa/descarga.
    if (!o.viewed_at) {
      try {
        await api.post(`/admin/orders/${o.id}/viewed`, { printed: false });
        const viewedAt = new Date().toISOString();
        setSelected((cur) => cur?.id === o.id ? { ...cur, viewed_at: viewedAt } : cur);
        setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, viewed_at: viewedAt } : x));
        setAskedPrinted(true);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "No se pudo marcar como visualizado");
      }
    }
  };

  const markStaffThreadRead = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/messages/mark-read`);
      if (data && data.id) {
        setSelected(data);
      } else {
        setSelected({ ...selected, has_unread_for_staff: false });
      }
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo marcar como leído");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Estado actualizado");
      await load();
      if (selected?.id === id) {
        const closed = ["En preparación", "Listo para imprimir", "Terminado", "Entregado", "Cancelado"].includes(status);
        setSelected({
          ...selected, status,
          is_open: closed ? false : selected.is_open,
          closed_at: closed ? new Date().toISOString() : selected.closed_at,
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markViewed = async (printed) => {
    if (!selected) return;
    try {
      await api.post(`/admin/orders/${selected.id}/viewed`, { printed });
      toast.success(printed ? "Pedido marcado como impreso" : "Pedido visualizado");
      setAskedPrinted(true);
      await load();
      const updated = printed
        ? { ...selected, is_open: false, viewed_at: new Date().toISOString(), closed_at: new Date().toISOString(), status: "Terminado" }
        : { ...selected, viewed_at: new Date().toISOString() };
      setSelected(updated);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const closeOrder = async (id) => {
    if (!window.confirm("¿Cerrar este pedido? El cliente ya no podrá añadir archivos.")) return;
    try {
      await api.post(`/admin/orders/${id}/close`);
      toast.success("Pedido cerrado");
      await load();
      if (selected?.id === id) setSelected({ ...selected, is_open: false, closed_at: new Date().toISOString() });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteFiles = async (id) => {
    if (!window.confirm("¿Eliminar archivos del pedido? El registro se conservará.")) return;
    try {
      await api.delete(`/admin/orders/${id}/files`);
      toast.success("Archivos eliminados");
      await load();
      if (selected?.id === id) setSelected({ ...selected, files: [] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("¿Eliminar el pedido COMPLETO (registro + archivos)? Esta acción es irreversible.")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Pedido eliminado");
      setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archiveOrder = async (id) => {
    if (!window.confirm("¿Archivar este pedido? Desaparecerá del listado principal, pero podrás verlo en Archivados.")) return;
    try {
      await api.post(`/admin/orders/${id}/archive`);
      toast.success("Pedido archivado");
      setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo archivar"); }
  };

  const reportIncident = async (id) => {
    const msg = window.prompt(
      "Mensaje de incidencia para el cliente:\n(ej: 'Tu archivo está corrupto, por favor reenvíalo')"
    );
    if (!msg || !msg.trim()) return;
    try {
      await api.post(`/admin/orders/${id}/incident`, { message: msg.trim() });
      toast.success("Incidencia notificada al cliente");
      await load();
      if (selected?.id === id) {
        setSelected({
          ...selected, status: "Incidencia", is_open: false,
          closed_at: new Date().toISOString(), incident_message: msg.trim(),
        });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };


  const sendStaffReply = async () => {
    if (!selected || !staffReply.trim()) return;
    setSendingReply(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/messages`, { body: staffReply.trim() });
      setSelected(data);
      setStaffReply("");
      toast.success("Respuesta enviada al cliente");
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar la respuesta");
    } finally {
      setSendingReply(false);
    }
  };

  const requestCounterCode = async (id) => {
    if (!window.confirm("¿Enviar al cliente el mensaje para que muestre su código en mostrador?")) return;
    try {
      const { data } = await api.post(`/admin/orders/${id}/request-counter-code`);
      setSelected(data);
      toast.success("Mensaje rápido enviado al cliente");
      await load(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar el mensaje rápido");
    }
  };

  const savePermanentNote = async () => {
    if (!selected?.user_id) {
      toast.error("Este pedido no tiene cliente registrado");
      return;
    }

    const text = (permanentNoteText || "").slice(0, 500);

    try {
      await api.patch(`/admin/clients/${selected.user_id}`, { notes: text });
      toast.success(text ? "Nota permanente guardada" : "Nota permanente eliminada");

      setSelected((cur) => cur ? { ...cur, client_internal_notes: text } : cur);
      setOrders((prev) => prev.map((o) =>
        o.user_id === selected.user_id ? { ...o, client_internal_notes: text } : o
      ));
      setEditingPermanentNote(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la nota permanente");
    }
  };

  const deletePermanentNote = async () => {
    if (!selected?.user_id) return;
    if (!window.confirm("¿Eliminar la nota permanente de este cliente?")) return;
    setPermanentNoteText("");
    try {
      await api.patch(`/admin/clients/${selected.user_id}`, { notes: "" });
      toast.success("Nota permanente eliminada");
      setSelected((cur) => cur ? { ...cur, client_internal_notes: "" } : cur);
      setOrders((prev) => prev.map((o) =>
        o.user_id === selected.user_id ? { ...o, client_internal_notes: "" } : o
      ));
      setEditingPermanentNote(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar la nota permanente");
    }
  };

  const downloadUrl = (orderId, filename) => {
    const t = localStorage.getItem("kapei_token");
    return `${API}/admin/orders/${orderId}/files/${encodeURIComponent(filename)}?token=${encodeURIComponent(t || "")}`;
  };

  const zipUrl = (orderId) => {
    const t = localStorage.getItem("kapei_token");
    return `${API}/admin/orders/${orderId}/files.zip?token=${encodeURIComponent(t || "")}`;
  };

  const clientActiveZipUrl = (orderId) => {
    const t = localStorage.getItem("kapei_token");
    return `${API}/admin/orders/${orderId}/client-active-files.zip?token=${encodeURIComponent(t || "")}`;
  };

  const openAllOrderFiles = (order) => {
    const files = order?.files || [];
    if (!files.length) {
      toast.error("Este pedido no tiene archivos");
      return;
    }

    files.forEach((f, idx) => {
      setTimeout(() => {
        window.open(downloadUrl(order.id, f.filename), "_blank", "noopener,noreferrer");
      }, idx * 600);
    });

    toast.success(`Abriendo ${files.length} archivo(s) sin ZIP. Si solo se abre uno, permite ventanas emergentes para app.kapei.es.`);
  };

  const selectedPaymentGroup = selected ? paymentGroups.find((g) =>
    g.orders.some((o) => o.id === selected.id)
  ) : null;

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Pedidos</h1>
          <p className="text-sm text-slate-600">Recibidos automáticamente desde la app cliente.</p>
        </div>
        <Button data-testid="reload-orders" variant="outline" onClick={load} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Recargar
        </Button>
      </div>

      {false && paymentGroups.length > 0 && (
        <div className="mb-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-widest text-emerald-700">
                Pagos agrupados sugeridos
              </div>
              <div className="text-sm font-bold text-emerald-950">
                Clientes con varios pedidos pendientes de pago. Puedes generar un único enlace y ahorrar comisiones.
              </div>
            </div>
          </div>

          <div className="space-y-2">
            {paymentGroups.slice(0, 5).map((g) => (
              <div key={g.key} className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-emerald-200">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-black text-slate-900">
                      {g.client_name} · {g.orders.length} pedidos
                    </div>
                    <div className="mt-1 text-xs font-bold text-slate-600">
                      {g.codes.join(" · ")}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs font-black uppercase tracking-widest text-emerald-700">
                        Total agrupado
                      </div>
                      <div className="text-2xl font-black text-emerald-950">
                        {g.total.toFixed(2)} €
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={() => createGroupedPayment(g)}
                      disabled={creatingPayment}
                      className="bg-[#4d7c0f] font-black hover:bg-[#3f6212]"
                    >
                      Crear pago agrupado
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {paymentGroups.length > 5 && (
            <div className="mt-2 text-xs font-bold text-emerald-800">
              Hay más clientes con pagos agrupables. Usa el buscador o revisa la lista.
            </div>
          )}
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="text-xs font-black uppercase tracking-widest text-red-700">Recogidas atrasadas</div>
          <div className="mt-1 text-2xl font-black text-red-900">
            {orders.filter((o) => o.pickup_ready && pickupUrgencyLabel(o.pickup_datetime) === "Recogida ATRASADA").length}
          </div>
        </div>
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
          <div className="text-xs font-black uppercase tracking-widest text-purple-700">Recogidas hoy</div>
          <div className="mt-1 text-2xl font-black text-purple-900">
            {orders.filter((o) => o.pickup_ready && pickupUrgencyLabel(o.pickup_datetime) === "Recoger HOY").length}
          </div>
        </div>
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
          <div className="text-xs font-black uppercase tracking-widest text-orange-700">Recogidas mañana</div>
          <div className="mt-1 text-2xl font-black text-orange-900">
            {orders.filter((o) => o.pickup_ready && pickupUrgencyLabel(o.pickup_datetime) === "Recoger MAÑANA").length}
          </div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="text-xs font-black uppercase tracking-widest text-red-700">Incidencias</div>
          <div className="mt-1 text-2xl font-black text-red-900">
            {orders.filter((o) => o.status === "Incidencia" || o.has_unread_for_staff).length}
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <Input
          data-testid="search-orders"
          placeholder="Buscar por código o nombre…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-9 max-w-xs"
        />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="filter-status" className="h-9 w-[220px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos sin archivar</SelectItem>
            <SelectItem value="archived">Archivados</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-bold text-slate-500">{filtered.length} pedido(s)</span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm" data-testid="orders-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="p-3">Código</th>
              <th className="p-3">Cliente</th>
              <th className="p-3">Fecha</th>
              <th className="p-3">Archivos</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Pedido</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-slate-500">Cargando…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500">No hay pedidos.</td></tr>
            )}
            {filtered.map((o) => {
              const open = isOpenOrder(o);
              return (
                <tr
                  key={o.id}
                  className={`border-t border-slate-100 hover:bg-slate-50 ${
                    !o.viewed_at ? "bg-lime-50/70 font-bold" : ""
                  }`}
                  data-testid={`row-${o.code}`}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-base font-black text-[#4d7c0f]">{o.code}</span>
                      {!o.viewed_at && (
                        <span className="rounded-full bg-lime-600 px-2 py-0.5 text-[10px] font-black uppercase text-white">
                          Nuevo
                        </span>
                      )}
                      {o.has_payment_update_for_staff && o.payment_status === "paid" && !["Terminado", "Entregado", "Cancelado"].includes(o.status) && (
                        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black uppercase text-white">
                          💳 Pedido pagado
                        </span>
                      )}
                      {o.has_unread_for_staff && (
                        <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase text-white">
                          Cliente respondió
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-bold text-slate-900">{o.client_name}</div>
                    {o.client_phone && <div className="text-xs text-slate-500">{o.client_phone}</div>}
                    {!o.user_id && <div className="text-[10px] uppercase tracking-widest text-slate-400">Invitado</div>}
                  </td>
                  <td className="p-3 whitespace-nowrap text-xs text-slate-600">
                    {new Date(o.created_at).toLocaleString("es-ES")}
                  </td>
                  <td className="p-3 text-xs">
                    {o.files?.length || 0} · {(o.total_size / 1024).toFixed(0)} KB
                    {o.page_count != null && <> · <strong>{o.page_count} pág.</strong></>}
                    {o.pickup_ready && (
                      <Badge className={`ml-2 ${pickupUrgencyClass(o.pickup_datetime)}`}>
                        <Calendar className="mr-1 h-3 w-3" />{pickupUrgencyLabel(o.pickup_datetime)}
                      </Badge>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge variant="outline" className={statusColors[o.status]}>{o.status}</Badge>
                  </td>
                  <td className="p-3">
                    {open ? (
                      <span data-testid={`flag-open-${o.code}`} className="inline-flex items-center gap-1 rounded-full bg-[#4d7c0f]/10 px-2 py-0.5 text-xs font-bold text-[#4d7c0f]">
                        <Unlock className="h-3 w-3" /> Abierto
                      </span>
                    ) : (
                      <span data-testid={`flag-closed-${o.code}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                        <Lock className="h-3 w-3" /> Cerrado
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <Button
                      data-testid={`open-${o.code}`}
                      variant="outline" size="sm"
                      onClick={() => openDialog(o)}
                      className="gap-1"
                    >
                      <Eye className="h-4 w-4" /> Ver
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl p-0">
          {selected && (
            <div className="max-h-[90vh] overflow-y-auto p-6">
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-3 text-2xl font-black">
                  <span className="font-mono text-[#4d7c0f]">{selected.code}</span>
                  <Badge variant="outline" className={statusColors[selected.status]}>{selected.status}</Badge>
                  {selected.has_payment_update_for_staff && selected.payment_status === "paid" && !["Terminado", "Entregado", "Cancelado"].includes(selected.status) && (
                    <Badge className="bg-emerald-600 text-white">💳 Pedido pagado</Badge>
                  )}
                  {selected.has_unread_for_staff && (
                    <Badge className="bg-red-600 text-white">Cliente respondió</Badge>
                  )}
                  {isOpenOrder(selected) ? (
                    <Badge className="bg-[#4d7c0f]/10 text-[#4d7c0f]"><Unlock className="mr-1 h-3 w-3" />Abierto</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700"><Lock className="mr-1 h-3 w-3" />Cerrado</Badge>
                  )}
                </DialogTitle>
              </DialogHeader>

              {/* Printed prompt - only shown the first time staff opens this order */}
              {!askedPrinted && selected.status !== "Terminado" && (
                <div data-testid="printed-prompt" className="rounded-xl border-2 border-[#4d7c0f]/30 bg-[#4d7c0f]/5 p-4">
                  <div className="mb-3 flex items-center gap-2 text-base font-extrabold text-slate-900">
                    <Printer className="h-5 w-5 text-[#4d7c0f]" />
                    ¿Has impreso este pedido?
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="printed-yes"
                      onClick={() => markViewed(true)}
                      className="bg-[#4d7c0f] hover:bg-[#3f6212]"
                    >
                      Sí — marcar como impreso
                    </Button>
                    <Button
                      data-testid="printed-no"
                      variant="outline"
                      onClick={() => markViewed(false)}
                    >
                      No — solo visualizado
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">
                    Al marcar “Sí” el pedido se cierra: el cliente no podrá añadir más archivos.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Cliente</div>
                  <div className="font-bold text-slate-900">{selected.client_name}</div>
                  {selected.client_phone && <div className="text-slate-600">{selected.client_phone}</div>}
                  {selected.client_email && <div className="text-slate-600">{selected.client_email}</div>}
                  {!selected.user_id && <div className="text-[10px] uppercase tracking-widest text-slate-400">Invitado</div>}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Fechas</div>
                  <div className="text-slate-700">Creado: {formatKapeiDateTime(selected.created_at)}</div>
                  {selected.last_upload_at && (
                    <div className="text-slate-700">Último envío: {formatKapeiDateTime(selected.last_upload_at)}</div>
                  )}
                  {selected.viewed_at && (
                    <div className="text-slate-700">Visto: {formatKapeiDateTime(selected.viewed_at)}</div>
                  )}
                  {selected.closed_at && (
                    <div className="text-slate-700">Cerrado: {formatKapeiDateTime(selected.closed_at)}</div>
                  )}
                  {selected.pickup_ready && (
                    <div className="mt-3 rounded-xl border border-purple-200 bg-purple-50 p-3 text-[#7c3aed]">
                      <div className="flex items-center gap-1 text-xs font-black uppercase tracking-widest">
                        <BadgeCheck className="h-4 w-4" /> Recogida preparada
                      </div>
                      {selected.pickup_datetime && (
                        <div className="mt-1 text-lg font-black">
                          {pickupUrgencyLabel(selected.pickup_datetime) !== "Recoger"
                            ? `${pickupUrgencyLabel(selected.pickup_datetime).replace("Recoger ", "")} · `
                            : ""}
                          {formatKapeiDateTime(selected.pickup_datetime)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {selected.user_id && (
                <div className={`rounded-xl border-2 p-3 text-sm shadow-sm ${
                  selected.client_internal_notes
                    ? "border-orange-300 bg-orange-50 text-orange-950"
                    : "border-slate-200 bg-slate-50 text-slate-700"
                }`}>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className={`text-xs font-black uppercase tracking-widest ${
                      selected.client_internal_notes ? "text-orange-700" : "text-slate-500"
                    }`}>
                      📝 Nota permanente del cliente
                    </div>
                    <div className="flex gap-2">
                      {selected.client_internal_notes && (
                        <button
                          type="button"
                          onClick={deletePermanentNote}
                          className="rounded-full bg-white px-3 py-1 text-[11px] font-black text-red-700"
                        >
                          Eliminar
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setPermanentNoteText(selected.client_internal_notes || "");
                          setEditingPermanentNote((v) => !v);
                        }}
                        className="rounded-full bg-white px-3 py-1 text-[11px] font-black text-slate-700"
                      >
                        {selected.client_internal_notes ? "Editar" : "Añadir nota permanente"}
                      </button>
                    </div>
                  </div>

                  {selected.client_internal_notes && !editingPermanentNote && (
                    <div className="max-h-12 overflow-y-auto whitespace-pre-line pr-1 text-xs font-semibold leading-relaxed">
                      {selected.client_internal_notes}
                    </div>
                  )}

                  {editingPermanentNote && (
                    <div className="mt-2">
                      <textarea
                        value={permanentNoteText}
                        maxLength={500}
                        onChange={(e) => setPermanentNoteText(e.target.value)}
                        className="min-h-[70px] w-full rounded-lg border border-orange-200 bg-white p-2 text-xs font-semibold text-slate-900 outline-none"
                        placeholder="Ej.: Premio 100 fotocopias B/N. Usadas 20. Quedan 80."
                      />
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-slate-500">
                          {permanentNoteText.length}/500 · Visible en futuros pedidos
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditingPermanentNote(false)}
                            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={savePermanentNote}
                            className="rounded-lg bg-[#4d7c0f] px-3 py-2 text-xs font-black text-white"
                          >
                            Guardar nota
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selected.promo_code && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-sm shadow-sm" data-testid="admin-promo-box">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="text-xs font-black uppercase tracking-widest text-amber-700">
                      🎁 PROMO APLICADA
                    </div>
                    <span className="rounded-full bg-amber-200 px-3 py-1 text-xs font-black text-amber-900">
                      {selected.promo_status === "redeemed" ? "Aplicada" : "Pendiente"}
                    </span>
                  </div>
                  <div className="text-lg font-black text-slate-900">
                    {selected.promo_title || selected.promo_code}
                  </div>
                  {selected.promo_gift && (
                    <div className="mt-1 text-slate-800">
                      Entregar/aplicar: <strong>{selected.promo_gift}</strong>
                    </div>
                  )}
                  {selected.promo_code?.startsWith("RAFFLE-") && (
                    <div className="mt-2 rounded-lg bg-white/70 p-2 text-xs font-bold text-amber-900">
                      Premio de sorteo: canjear completo en este pedido. Si el pedido es inferior al valor del premio, la parte no utilizada se pierde.
                    </div>
                  )}
                  <div className="mt-1 text-xs text-slate-600">
                    Código: <strong>{selected.promo_code}</strong>
                  </div>
                </div>
              )}

              <div className="rounded-xl border-2 border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2 text-xs font-black uppercase tracking-widest text-slate-600">
                  <span className="text-[#4d7c0f]">📁 Archivos del pedido ({selected.files?.length || 0})</span>
                  <div className="flex items-center gap-2">
                    {selected.page_count != null && (
                      <span data-testid="admin-page-total" className="text-[#4d7c0f]">
                        Total: {selected.page_count} página{selected.page_count === 1 ? "" : "s"}
                      </span>
                    )}
                    {(selected.files?.length || 0) > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => openAllOrderFiles(selected)}
                        className="h-7 px-2 text-[10px] font-black"
                      >
                        Abrir todos sin ZIP
                      </Button>
                    )}
                  </div>
                </div>
                <ul className="flex max-h-56 flex-col gap-3 overflow-y-auto">
                  {(selected.files || []).map((f) => (
                    <li key={f.filename} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-bold text-slate-800">{f.filename}</div>
                        <div className="text-xs text-slate-500">
                          {(f.size / 1024).toFixed(0)} KB
                          {typeof f.pages === "number" && ` · ${f.pages} pág.`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          data-testid={`download-${f.filename}`}
                          href={downloadUrl(selected.id, f.filename)}
                          target="_blank" rel="noreferrer"
                          className="rounded-lg bg-slate-900 px-2 py-1 text-xs font-bold text-white"
                        >
                          <Download className="inline h-3 w-3" /> Abrir
                        </a>
                      </div>
                    </li>
                  ))}
                  {(!selected.files || selected.files.length === 0) && (
                    <li className="text-xs italic text-slate-500">Archivos eliminados.</li>
                  )}
                </ul>
              </div>

              <div className="rounded-xl border-2 border-slate-200 bg-white p-3 text-sm shadow-sm">
                <div className="mb-2 text-xs font-black uppercase tracking-widest text-slate-600">
                  📝 Indicaciones del cliente
                </div>

                {selected.options ? (
                  <div className="grid grid-cols-2 gap-1 text-slate-700">
                    <div>Copias: <strong>{selected.options.copies}</strong></div>
                    <div>Tinta: <strong>{selected.options.color === "bw" ? "B/N" : "Color"}</strong></div>
                    <div>Caras: <strong>{selected.options.sides === "single" ? "Una" : "Dos"}</strong></div>
                    <div>Tamaño: <strong>{selected.options.size}</strong></div>
                    <div>Papel: <strong>{selected.options.paper}</strong></div>
                  </div>
                ) : (
                  <div className="rounded-lg bg-orange-50 p-2 text-xs font-bold text-orange-800">
                    ⚠ Pedido rápido: el cliente no indicó configuración de impresión.
                  </div>
                )}

                {selected.note && (
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Nota del cliente</div>
                    <div className="mt-1 text-sm italic text-slate-800">
                      "{selected.note}"
                    </div>
                  </div>
                )}
              </div>

              <div className={`rounded-xl border-2 p-3 text-sm shadow-sm ${
                productionInstructions
                  ? "border-lime-300 bg-lime-50"
                  : "border-slate-200 bg-white"
              }`}>
                <button
                  type="button"
                  onClick={() => setShowProductionInstructions((v) => !v)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <span className={`text-xs font-black uppercase tracking-widest ${
                    productionInstructions ? "text-lime-800" : "text-slate-500"
                  }`}>
                    📝 Observaciones KAPEI
                  </span>
                  <span className="rounded-full bg-white px-3 py-1 text-[10px] font-black text-slate-600">
                    {showProductionInstructions ? "Ocultar" : productionInstructions ? "Editar" : "+"}
                  </span>
                </button>

                {productionInstructions && !showProductionInstructions && (
                  <div className="mt-2 whitespace-pre-wrap rounded-lg bg-white/70 p-2 text-sm font-semibold text-lime-900">
                    {productionInstructions}
                  </div>
                )}

                {showProductionInstructions && (
                  <div className="mt-3">
                    <textarea
                      value={productionInstructions}
                      onChange={(e) => setProductionInstructions(e.target.value)}
                      placeholder="Ej.: Encuadernar tapa dura / cambiar a papel 120 g / confirmado por teléfono."
                      className="min-h-[80px] w-full rounded-lg border border-lime-200 bg-white p-2 text-sm outline-none focus:ring-2 focus:ring-lime-300"
                    />
                    <div className="mt-2 text-xs font-semibold text-slate-500">
                      Se guardan al pulsar Guardar en la pestaña Cobro y aparecerán en ZIP/PRESUPUESTO.
                    </div>
                  </div>
                )}
              </div>


              <div className="rounded-xl border-2 border-slate-200 bg-slate-50 p-4 text-sm shadow-sm">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-xs font-black uppercase tracking-widest text-slate-600">
                      🧾 Otros trabajos del cliente
                    </div>
                    <div className="mt-1 text-xs font-semibold text-slate-500">
                      Producción para preparar trabajos. Cobro para importes y enlaces.
                    </div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${
                    selected.is_reliable || selected.client_is_reliable || selected.user_is_reliable
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-white text-slate-500"
                  }`}>
                    {selected.is_reliable || selected.client_is_reliable || selected.user_is_reliable
                      ? "Cliente fiable"
                      : "No entregados"}
                  </span>
                </div>

                <div className="mb-3 grid grid-cols-2 gap-2 rounded-xl bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setClientWorkTab("production")}
                    className={`rounded-lg px-3 py-2 text-xs font-black uppercase ${
                      clientWorkTab === "production"
                        ? "bg-[#4d7c0f] text-white"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    🖨️ Producción
                  </button>
                  <button
                    type="button"
                    onClick={() => setClientWorkTab("payment")}
                    className={`rounded-lg px-3 py-2 text-xs font-black uppercase ${
                      clientWorkTab === "payment"
                        ? "bg-blue-700 text-white"
                        : "text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    💳 Cobro
                  </button>
                </div>

                {clientWorkTab === "production" && (
                  (() => {
                    const analysis = productionAnalysisForOrders(clientAnalysisOrders, isPremiumClient(selected));
                    return (
                      <div className={`mb-3 rounded-xl border-2 p-3 text-xs ${
                        analysis.signalRecommended
                          ? "border-orange-300 bg-orange-50 text-orange-900"
                          : "border-emerald-200 bg-emerald-50 text-emerald-900"
                      }`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-black uppercase tracking-widest">
                            {isPremiumClient(selected) ? "🟢 Cliente seguro" : "🟡 Cliente Normal"}
                          </div>
                          <div className="font-black uppercase">
                            {analysis.signalRecommended ? "⚠ Señal recomendada" : "✓ Señal no necesaria"}
                          </div>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 font-bold md:grid-cols-5">
                          <div>{analysis.orders} pedido{analysis.orders === 1 ? "" : "s"}</div>
                          <div>{analysis.filePages} pág. archivo</div>
                          <div>{analysis.printedUnits} impresiones</div>
                          <div>{analysis.bwUnits} B/N</div>
                          <div>{analysis.colorUnits} color</div>
                        </div>
                        {analysis.unspecifiedUnits > 0 && (
                          <div className="mt-2 text-[11px] font-black uppercase text-orange-800">
                            ⚠ {analysis.unspecifiedUnits} impresiones sin especificar
                          </div>
                        )}
                        <div className="mt-2 text-[10px] font-black uppercase tracking-widest opacity-80">
                          Carga producción: {analysis.level}
                        </div>
                      </div>
                    );
                  })()
                )}

                {clientWorkTab === "payment" && selected?.last_commercial_basket_code && (
                  <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-bold text-emerald-900">
                    Último presupuesto guardado:{" "}
                    <span className="font-black">{selected.last_commercial_basket_code}</span>
                    {selected.last_commercial_basket_total_eur ? (
                      <span> · {Number(selected.last_commercial_basket_total_eur || 0).toFixed(2).replace(".", ",")} €</span>
                    ) : null}
                  </div>
                )}

                {clientWorkTab === "payment" && (
                  (() => {
                    const commercialGroups = commercialGroupsForOrders(clientAnalysisOrders, pricingMode);
                    return (
                      <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          <div className="font-black uppercase tracking-widest">
                            Modo de cálculo comercial
                          </div>
                          <div className="flex rounded-lg bg-white p-1">
                            <button
                              type="button"
                              onClick={() => setPricingMode("grouped")}
                              className={`rounded-md px-3 py-1 text-[11px] font-black ${
                                pricingMode === "grouped" ? "bg-blue-700 text-white" : "text-blue-700"
                              }`}
                            >
                              Agrupado
                            </button>
                            <button
                              type="button"
                              onClick={() => setPricingMode("separate")}
                              className={`rounded-md px-3 py-1 text-[11px] font-black ${
                                pricingMode === "separate" ? "bg-blue-700 text-white" : "text-blue-700"
                              }`}
                            >
                              Por pedido
                            </button>
                          </div>
                        </div>

                        {(() => {
                          const quote = automaticQuoteForGroups(commercialGroups, serviceCatalog);
                          const extrasTotal = commercialExtras.reduce((sum, x) => sum + Number(x.amount || 0), 0);
                          const basketTotal = Math.round((Number(quote.total || 0) + extrasTotal) * 100) / 100;
                          window.__kapeiAutoQuoteByOrderId = quote.byOrderId || {};
                          return (
                            <div className="space-y-1">
                              {quote.groupQuotes.map(({ group: g, price }) => (
                                <div key={g.id} className="rounded-lg bg-white/80 px-3 py-2 font-bold">
                                  <div className="flex flex-wrap justify-between gap-2">
                                    <span>{g.label} · {g.orderCount} pedido{g.orderCount === 1 ? "" : "s"}</span>
                                    <span>{g.printedUnits} imp. · {g.bwUnits} B/N · {g.colorUnits} color</span>
                                  </div>

                                  {price.lines.length > 0 && (
                                    <div className="mt-2 rounded-lg bg-blue-50 p-2 text-[11px] text-blue-900">
                                      {price.lines.map((line, idx) => (
                                        <div key={idx} className="flex flex-wrap justify-between gap-2">
                                          <span>{line.label}: {line.qty} × {line.unitPrice.toFixed(2)} €</span>
                                          <span>{line.amount.toFixed(2).replace(".", ",")} €</span>
                                        </div>
                                      ))}
                                      <div className="mt-1 flex justify-between border-t border-blue-200 pt-1 font-black">
                                        <span>Subtotal presupuesto</span>
                                        <span>{price.total.toFixed(2).replace(".", ",")} €</span>
                                      </div>
                                    </div>
                                  )}

                                  {g.unspecifiedUnits > 0 && (
                                    <div className="mt-1 text-orange-700">
                                      ⚠ {g.unspecifiedUnits} impresiones sin especificar
                                    </div>
                                  )}
                                </div>
                              ))}

                              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-3">
                                <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                  Añadir al presupuesto
                                </div>

                                {commercialExtras.length > 0 && (
                                  <div className="mb-2 space-y-1">
                                    {commercialExtras.map((extra) => (
                                      <div key={extra.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2 py-1 text-[11px] font-bold">
                                        <span>
                                          {extra.concept}
                                          {extra.type === "catalog" && (
                                            <span className="ml-1 text-slate-400">
                                              · {extra.qty} × {Number(extra.unitPrice || 0).toFixed(2).replace(".", ",")} €
                                            </span>
                                          )}
                                        </span>
                                        <span className="flex items-center gap-2">
                                          {Number(extra.amount || 0).toFixed(2).replace(".", ",")} €
                                          <button
                                            type="button"
                                            onClick={() => removeCommercialExtra(extra.id)}
                                            className="font-black text-red-600"
                                          >
                                            ×
                                          </button>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                  <div className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    Añadir servicio del catálogo
                                  </div>

                                  {!basketSelectedService ? (
                                    <div className="relative">
                                      <input
                                        value={basketSearch}
                                        onChange={(e) => setBasketSearch(e.target.value)}
                                        placeholder="Buscar servicio: plastificado, foto, cartulina, diseño..."
                                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-xs"
                                      />
                                      {basketSearch.trim() && (
                                        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                                          {(serviceCatalog || [])
                                            .filter((item) => {
                                              const q = basketSearch.trim().toLowerCase();
                                              return [
                                                item.name,
                                                item.slug,
                                                item.category,
                                              ].some((v) => String(v || "").toLowerCase().includes(q));
                                            })
                                            .slice(0, 10)
                                            .map((item) => (
                                              <button
                                                key={item.id || item.slug}
                                                type="button"
                                                onClick={() => selectBasketService(item)}
                                                className="block w-full rounded-lg px-3 py-2 text-left text-xs hover:bg-lime-50"
                                              >
                                                <div className="font-black text-slate-900">
                                                  {item.icon || "📦"} {item.name}
                                                </div>
                                                <div className="text-[10px] font-bold text-slate-500">
                                                  {item.category || "general"} · {item.pricing_type || "precio"} · {Number(item.price_eur || 0).toFixed(2)} €
                                                </div>
                                              </button>
                                            ))}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-xs font-bold">
                                        <span>{basketSelectedService.icon || "📦"} {basketSelectedService.name}</span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setBasketSelectedService(null);
                                            setBasketUnitPrice("");
                                          }}
                                          className="font-black text-red-600"
                                        >
                                          Cambiar
                                        </button>
                                      </div>
                                      <div className="grid gap-2 md:grid-cols-[100px_140px_auto]">
                                        <input
                                          value={basketQty}
                                          onChange={(e) => setBasketQty(e.target.value)}
                                          placeholder="Cantidad"
                                          className="rounded-md border border-slate-200 px-3 py-2 text-xs"
                                        />
                                        <input
                                          value={basketUnitPrice}
                                          onChange={(e) => setBasketUnitPrice(e.target.value)}
                                          placeholder="Precio unidad"
                                          className="rounded-md border border-slate-200 px-3 py-2 text-xs"
                                        />
                                        <button
                                          type="button"
                                          onClick={addCatalogBasketLine}
                                          className="rounded-md bg-[#4d7c0f] px-3 py-2 text-[10px] font-black uppercase text-white"
                                        >
                                          Añadir servicio
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="grid gap-2 md:grid-cols-[1fr_120px_auto]">
                                  <input
                                    value={extraConcept}
                                    onChange={(e) => setExtraConcept(e.target.value)}
                                    placeholder="Extra manual: funda, arreglo, material no listado..."
                                    className="rounded-md border border-slate-200 px-3 py-2 text-xs"
                                  />
                                  <input
                                    value={extraAmount}
                                    onChange={(e) => setExtraAmount(e.target.value)}
                                    placeholder="€"
                                    className="rounded-md border border-slate-200 px-3 py-2 text-xs"
                                  />
                                  <button
                                    type="button"
                                    onClick={addCommercialExtra}
                                    className="rounded-md bg-slate-900 px-3 py-2 text-[10px] font-black uppercase text-white"
                                  >
                                    Añadir manual
                                  </button>
                                </div>
                              </div>

                              {(quote.total > 0 || commercialExtras.length > 0) && (
                                <div className="mt-2 rounded-xl border-2 border-blue-300 bg-white p-3">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div>
                                      <div className="text-[10px] font-black uppercase tracking-widest text-blue-700">
                                        Presupuesto automático
                                      </div>
                                      <div className="text-lg font-black text-blue-950">
                                        {basketTotal.toFixed(2).replace(".", ",")} €
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => saveCommercialBasket({ quote, commercialGroups, basketTotal })}
                                        disabled={savingBasket}
                                        className="rounded-lg bg-slate-900 px-3 py-2 text-[10px] font-black uppercase text-white disabled:opacity-60"
                                      >
                                        {savingBasket ? "Guardando…" : "Guardar presupuesto"}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const formatted = String(basketTotal.toFixed(2)).replace(".", ",");
                                          if (pricingMode === "grouped") {
                                            setPaymentAmount(formatted);
                                            setPaymentDescription(`Pago agrupado KAPEI: ${(clientAnalysisOrders || []).map((o) => o.code).filter(Boolean).join(", ")}`);
                                          } else if ((commercialGroups || []).length === 1) {
                                            setWorkAmount(formatted);
                                          } else {
                                            setPaymentAmount(formatted);
                                            setPaymentDescription(`Pago KAPEI: ${(clientAnalysisOrders || []).map((o) => o.code).filter(Boolean).join(", ")}`);
                                          }
                                        }}
                                        className="rounded-lg bg-blue-700 px-3 py-2 text-[10px] font-black uppercase text-white"
                                      >
                                        Usar para pago online
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()
                )}

                {clientWorkTab === "payment" && (
                <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                  <div>
                    <label className="text-xs font-bold uppercase text-slate-500">Importe total de este trabajo</label>
                    <Input
                      value={workAmount}
                      onChange={(e) => setWorkAmount(e.target.value)}
                      placeholder="Ej.: 38,00"
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={saveOrderAmount}
                      disabled={savingAmount}
                      className="w-full font-black"
                    >
                      {savingAmount ? "Guardando…" : "Guardar"}
                    </Button>
                  </div>
                </div>
                )}

                {siblingOrders.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 text-xs font-black uppercase tracking-widest text-slate-500">
                      <span>{clientWorkTab === "production" ? "Otros pedidos del cliente" : "Cobro del cliente"}</span>
                      {clientWorkTab === "production" && (
                        <a
                          href={clientActiveZipUrl(selected.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="rounded-lg bg-slate-900 px-3 py-1 text-[10px] font-black text-white"
                          onClick={(e) => e.stopPropagation()}
                        >
                          ZIP cliente
                        </a>
                      )}
                    </div>
                    <div className="max-h-60 space-y-1 overflow-y-auto pr-1">
                      {siblingOrders.map((o) => {
                        const paid = o.payment_status === "paid";
                        const workAmount = Number(o.work_amount_eur || 0);
                        const paymentAmount = Number(o.payment_amount_eur || 0);
                        const amount = workAmount > 0 ? workAmount : paymentAmount;
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => openSiblingOrder(o.id)}
                            className={`w-full rounded-lg border px-3 py-2 text-left text-xs hover:bg-slate-50 ${
                              o.id === selected.id ? "border-lime-300 bg-lime-50" : "border-slate-200 bg-white"
                            }`}
                          >
                            <div className="grid grid-cols-[82px_1fr_54px_76px_70px] items-center gap-2">
                              <div className="font-mono text-sm font-black text-[#4d7c0f]">{o.code}</div>
                              <div className="min-w-0 truncate font-bold text-slate-700">
                                {o.status || "Sin estado"}{o.id === selected.id ? " · actual" : ""}
                              </div>
                              <div className="text-right font-semibold text-slate-500">
                                {typeof o.page_count === "number" ? (
                                  <>
                                    <div>{o.page_count} pág.</div>
                                    <div className="text-[10px] font-black text-slate-400">
                                      {Math.round(Number(o.page_count || 0) * _copies(o))} imp.
                                    </div>
                                  </>
                                ) : "—"}
                              </div>
                              <div className="text-right">
                                <div className={`text-sm font-black ${amount > 0 ? "text-slate-900" : "text-red-700"}`}>
                                  {amount.toFixed(2)} €
                                </div>
                                <div className={`text-[8px] font-black uppercase leading-none ${
                                  paid ? "text-emerald-700" : amount > 0 ? "text-blue-700" : "text-red-700"
                                }`}>
                                  {paid ? "Cobrado" : workAmount > 0 ? "Con importe" : paymentAmount > 0 ? "Solicitado" : (pricingMode === "separate" && window.__kapeiAutoQuoteByOrderId?.[o.id] ? `Sugerido ${Number(window.__kapeiAutoQuoteByOrderId[o.id]).toFixed(2)} €` : "Sin importe")}
                                </div>
                              </div>
                              <div className="text-right">
                                {(o.files?.length || 0) > 0 && (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openAllOrderFiles(o);
                                    }}
                                    className="inline-flex rounded-md bg-slate-900 px-2 py-1 text-[9px] font-black uppercase text-white"
                                  >
                                    Abrir
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-3 rounded-xl bg-white p-3 text-right">
                      {clientWorkTab === "production" ? (
                        <>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Producción
                          </div>
                          <div className="text-sm font-black text-slate-900">
                            {siblingOrders.length} pedido{siblingOrders.length === 1 ? "" : "s"} · {siblingOrders.reduce((sum, o) => sum + Number(o.page_count || 0), 0)} página{siblingOrders.reduce((sum, o) => sum + Number(o.page_count || 0), 0) === 1 ? "" : "s"}
                          </div>
                          <div className="mt-1 text-xs font-bold text-slate-500">
                            {siblingOrders.filter((o) => o.payment_status === "paid").length} cobrado(s) · {siblingOrders.filter((o) => Number(o.work_amount_eur || 0) <= 0).length} sin importe
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Cobro
                          </div>
                          <div className="text-sm font-bold text-slate-500">
                            {siblingOrders.filter((o) => Number(o.work_amount_eur || 0) > 0).length} con importe · {siblingOrders.filter((o) => Number(o.work_amount_eur || 0) <= 0).length} sin importe · {siblingOrders.filter((o) => o.payment_status === "paid").length} cobrado(s)
                          </div>
                          <div className="text-2xl font-black text-slate-900">
                            {siblingOrders
                              .filter((o) => o.payment_status !== "paid")
                              .reduce((sum, o) => sum + Number(o.work_amount_eur || 0), 0)
                              .toFixed(2)} €
                          </div>
                          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            Pendiente con importe
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl bg-white p-3 text-xs font-semibold text-slate-500">
                    No hay otros pedidos recientes de este cliente.
                  </div>
                )}
              </div>


              <div className={`rounded-xl border-2 p-4 text-sm shadow-sm ${
                selected.payment_status === "paid"
                  ? "border-emerald-300 bg-emerald-50"
                  : selected.payment_status === "pending"
                    ? "border-blue-300 bg-blue-50"
                    : "border-slate-200 bg-white"
              }`}>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="text-xs font-black uppercase tracking-widest text-blue-700">
                    💳 Pago online
                  </div>
                  {selected.payment_status && (
                    <span className={`rounded-full px-3 py-1 text-xs font-black ${
                      selected.payment_status === "paid"
                        ? "bg-emerald-200 text-emerald-900"
                        : "bg-blue-200 text-blue-900"
                    }`}>
                      {selected.payment_status === "paid" ? "Pagado" : "Pendiente"}
                    </span>
                  )}
                </div>

                {selected.payment_status === "paid" ? (
                  <div className="text-sm font-bold text-emerald-900">
                    Pago recibido: {Number(selected.payment_amount_eur || 0).toFixed(2)} €
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-bold uppercase text-slate-500">Importe total</label>
                        <Input
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="Ej.: 12,50"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase text-slate-500">Descripción</label>
                        <Input
                          value={paymentDescription}
                          onChange={(e) => setPaymentDescription(e.target.value)}
                          placeholder={`Pedido KAPEI ${selected.code}`}
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="text-xs font-bold uppercase text-slate-500">Tipo de pago</label>
                        <select
                          value={paymentType}
                          onChange={(e) => setPaymentType(e.target.value)}
                          className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm font-bold"
                        >
                          <option value="full">Pago completo</option>
                          <option value="deposit">Señal / reserva</option>
                          <option value="partial">Pago parcial</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase text-slate-500">Texto visible para el cliente</label>
                        <Input
                          value={paymentNote}
                          onChange={(e) => setPaymentNote(e.target.value)}
                          placeholder="Ej.: Señal de 15 €. Total estimado 45 €."
                          className="mt-1"
                        />
                      </div>
                    </div>

                    {selected.payment_checkout_url && (
                      <div className="space-y-2 rounded-xl border border-blue-200 bg-white p-3">
                        <div className="text-xs font-black uppercase tracking-widest text-blue-700">
                          Enlace para enviar al cliente
                        </div>
                        <div className="break-all rounded-lg bg-blue-50 p-2 text-xs font-bold text-blue-900">
                          {getPublicPaymentUrl(selected)}
                        </div>
                        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={copyPublicPaymentUrl}
                            className="font-black"
                          >
                            Copiar enlace KAPEI
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={openPaymentWhatsApp}
                            className="font-black"
                          >
                            WhatsApp
                          </Button>
                          <a
                            href={selected.payment_checkout_url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md border border-slate-200 px-3 py-2 text-center text-sm font-black text-blue-700 hover:bg-slate-50"
                          >
                            Abrir Stripe
                          </a>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={cancelPaymentRequest}
                            disabled={creatingPayment}
                            className="font-black text-red-700 hover:bg-red-50 md:col-span-3"
                          >
                            Cancelar solicitud de pago
                          </Button>
                        </div>
                      </div>
                    )}

                    <Button
                      type="button"
                      onClick={createPaymentRequest}
                      disabled={creatingPayment}
                      className="w-full bg-blue-700 font-black hover:bg-blue-800"
                    >
                      {creatingPayment ? "Creando enlace…" : "Solicitar pago al cliente"}
                    </Button>

                    <p className="text-xs text-slate-600">
                      El cliente recibirá una notificación y podrá pagar con tarjeta mediante Stripe Checkout seguro.
                    </p>
                  </div>
                )}
              </div>


              {(selected.thread?.length > 0 || selected.status === "Incidencia") && (
                <div className="rounded-xl border-2 border-slate-200 bg-white p-3">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
                      <MessageCircle className="h-4 w-4" /> Conversación con el cliente
                    </div>
                    {selected.has_unread_for_staff && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={markStaffThreadRead}
                        className="h-8 border-red-200 text-xs font-bold text-red-700 hover:bg-red-50"
                      >
                        Marcar conversación leída
                      </Button>
                    )}
                  </div>

                  {selected.thread?.length > 0 ? (
                    <ul className="mb-3 max-h-60 space-y-2 overflow-y-auto">
                      {selected.thread.map((m) => {
                        const isClient = m.author_role === "client";
                        return (
                          <li
                            key={m.id}
                            className={`rounded-xl border-2 p-3 text-sm ${
                              isClient
                                ? "border-[#4d7c0f]/30 bg-[#4d7c0f]/5 text-slate-900"
                                : "border-rose-200 bg-rose-50 text-rose-900"
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-2 text-[10px] font-black uppercase tracking-widest">
                              <span>{isClient ? (m.author_name || "Cliente") : "KAPEI"}</span>
                              <span className="text-slate-400">{new Date(m.created_at).toLocaleString("es-ES")}</span>
                            </div>
                            <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="mb-3 text-sm italic text-slate-500">Aún no hay mensajes en este pedido.</p>
                  )}

                  <div className="flex gap-2">
                    <Input
                      value={staffReply}
                      onChange={(e) => setStaffReply(e.target.value)}
                      placeholder="Responder al cliente…"
                      className="h-10"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendStaffReply();
                        }
                      }}
                    />
                    <Button
                      onClick={sendStaffReply}
                      disabled={sendingReply || !staffReply.trim()}
                      className="bg-[#4d7c0f] text-white hover:bg-[#3f6212]"
                    >
                      <Send className="mr-2 h-4 w-4" /> Enviar
                    </Button>
                  </div>
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="delete-files"
                    variant="outline"
                    onClick={() => deleteFiles(selected.id)}
                    className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" /> Borrar archivos
                  </Button>
                  {["Terminado", "Entregado", "Cancelado"].includes(selected.status) && !selected.archived_by_staff && (
                    <Button
                      data-testid="archive-order"
                      variant="outline"
                      onClick={() => archiveOrder(selected.id)}
                      className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
                    >
                      <Lock className="h-4 w-4" /> Archivar
                    </Button>
                  )}
                  <Button
                    data-testid="delete-order"
                    variant="outline"
                    onClick={() => deleteOrder(selected.id)}
                    className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" /> Eliminar pedido
                  </Button>
                  <Button
                    data-testid="report-incident"
                    variant="outline"
                    onClick={() => reportIncident(selected.id)}
                    className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                  >
                    <AlertCircle className="h-4 w-4" /> Marcar incidencia
                  </Button>
                  <Button
                    data-testid="request-counter-code"
                    variant="outline"
                    onClick={() => requestCounterCode(selected.id)}
                    className="gap-2 border-lime-300 text-lime-800 hover:bg-lime-50"
                  >
                    <Send className="h-4 w-4" /> Pedir código mostrador
                  </Button>
                  {isOpenOrder(selected) && (
                    <Button
                      data-testid="close-order"
                      variant="outline"
                      onClick={() => closeOrder(selected.id)}
                      className="gap-2"
                    >
                      <X className="h-4 w-4" /> Cerrar pedido
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger data-testid="change-status" className="w-[200px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
